import React, { createContext, useState, useEffect } from 'react';
import axios from '../api/axiosConfig';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [apiLogs, setApiLogs] = useState([]);

  const addApiLog = async (cuplNumber, type, detail, status = 'info') => {
    // Agregar localmente para feedback inmediato
    const newLog = {
      id: Date.now(),
      time: new Date().toLocaleTimeString(),
      cupl: cuplNumber,
      type,
      detail,
      status
    };
    setApiLogs(prev => [newLog, ...prev].slice(0, 50));

    // Guardar en base de datos
    try {
      if (token) {
        await axios.post('/api/payment-logs', {
          cupl_number: cuplNumber,
          action_type: type,
          detail: detail,
          status: status
        });
      }
    } catch (error) {
      console.error('Error al guardar log en DB:', error);
    }
  };

  const clearApiLogs = () => setApiLogs([]);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        setToken(storedToken);
        try {
          // Verificar token y obtener datos actualizados del usuario (incluyendo módulos)
          const response = await axios.get('/api/auth/me', {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          const userData = response.data;
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        } catch (error) {
          console.error('Error al verificar sesión:', error);
          // Si el token no es válido o hay error, limpiar
          if (error.response?.status === 401) {
            logout();
          } else {
            // Si es otro error (ej. red), usar lo que hay en localStorage temporalmente
            const storedUser = localStorage.getItem('user');
            if (storedUser) setUser(JSON.parse(storedUser));
          }
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/auth/login', { username, password });
      const { token, user } = response.data;

      if (!token || !user) {
        console.error('Invalid login response structure:', response.data);
        throw new Error('Respuesta inválida del servidor: falta token o usuario');
      }

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      setToken(token);
      setUser(user);
      
      return user;
    } catch (error) {
      console.error('Login error:', error);
      if (error.response?.status === 401) {
        throw 'Usuario o contraseña inválidos';
      }
      throw error.response?.data?.message || error.message || 'Error al iniciar sesión';
    }
  };

  const register = async (username, email, password) => {
    try {
      const response = await axios.post('/api/auth/register', {
        username,
        email,
        password,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Error al registrar usuario';
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, token, login, register, logout, loading, apiLogs, addApiLog, clearApiLogs }}>
      {children}
    </AuthContext.Provider>
  );
};
